import React, { useState, useEffect, useRef, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { logoutRequest } from '../logout/operations'
import { AppContext } from '../App'
import { clearProjectAction, cloneProject, createNewProject, deleteProject, fetchProject } from '../project'
import { NewProjectIcon } from '../icons/NewProjectIcon'
import { HomeIcon } from '../icons/HomeIcon'
import { HistoryIcon } from '../icons/HistoryIcon'
import { LogoutIcon } from '../icons/LogoutIcon'
import { useAsyncError } from '../errorBoundary/useAsyncError'
import { DeleteIcon } from '../icons/DeleteIcon'
import { CloneIcon } from '../icons/CloneIcon'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'

const BurgerMenu = () => {
  const menuRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { state, dispatch } = useContext(AppContext)
  const [isOpen, setIsOpen] = useState(false)
  const [shouldNavigate, setShouldNavigate] = useState(false)
  const [shouldNavigateToClone, setShouldNavigateToClone] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const throwAsyncError = useAsyncError()

  const toggleMenu = () => setIsOpen(prev => !prev)

  const handleOutsideClick = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }
  useEffect(() => {
    if (shouldNavigateToClone && state.clonedProjectId) {
      fetchProject(dispatch, { projectId: state.clonedProjectId }, throwAsyncError)

      navigate(`/project/${state.clonedProjectId}`)
      setShouldNavigateToClone(false)
    }
  }, [state.clonedProjectId, shouldNavigateToClone])

  useEffect(() => {
    if (shouldNavigate && state.currentProject.projectId) {
      fetchProject(dispatch, { projectId: state.currentProject.projectId }, throwAsyncError)

      navigate(`/project/${state.currentProject.projectId}`)
      setShouldNavigate(false)
    }
  }, [state?.currentProject.projectId, shouldNavigate])

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const actions = {
    logout: async () => {
      await logoutRequest(dispatch)
    },
    newProject: async () => {
      await createNewProject(dispatch, throwAsyncError)
      setShouldNavigate(true)
    },
    history: () => {
      navigate('/history')
    },
    delete: async () => {
      await deleteProject(dispatch, { projectId: state.currentProject?.projectId }, throwAsyncError)
      navigate('/')
    },
    clone: async () => {
      await cloneProject(dispatch, { projectId: state.currentProject?.projectId }, throwAsyncError)
      setShouldNavigateToClone(true)
    },
    home: () => {
      dispatch(clearProjectAction())
      navigate('/')
    }
  }

  const handleAction = async (action) => {
    await actions[action]()
    toggleMenu()
  }

  if (!state.isLoggedIn) return null

  const menuItems = [
    { label: 'Home', action: 'home', icon: <HomeIcon />, shouldDisplay: location.pathname !== '/' },
    { label: 'History', action: 'history', icon: <HistoryIcon />, shouldDisplay: location.pathname !== '/history' },
    { label: 'New Project', action: 'newProject', icon: <NewProjectIcon /> },
    { label: 'Clone Project', action: 'clone', icon: <CloneIcon />, shouldDisplay: location.pathname.startsWith('/project') },
    { label: 'Delete Project', action: 'delete', icon: <DeleteIcon />, shouldDisplay: location.pathname.startsWith('/project') },
    { label: 'Logout', action: 'logout', icon: <LogoutIcon /> }
  ]

  return (
    <div className='fixed top-4 right-4 z-40'>
      <button className={`p-2 ${isOpen ? 'rounded-tl-lg rounded-tr-lg' : 'rounded-lg'} bg-slateDark hover:bg-gray-700`} onClick={toggleMenu}>
        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
          <path fill='none' stroke='#a6adba' strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.5' d='M3 6.001h18m-18 6h18m-18 6h18' />
        </svg>
      </button>

      {isOpen && (
        <nav ref={menuRef} className='absolute top-10 right-0 bg-slateDark shadow-md w-48 z-10 rounded-lg rounded-tr-none'>
          <ul className='flex flex-col p-2 overflow-y-auto max-h-[60vh]'>
            {menuItems.map(
              ({ label, action, icon, shouldDisplay = true }) =>
                shouldDisplay && (
                  <li key={label}>
                    <button
                      className={`block px-4 py-2 flex items-center rounded-lg w-full text-left transition-colors duration-200
                  ${label === 'Delete Project'
                          ? 'text-red-400 hover:bg-red-400 hover:text-slateDark'
                          : 'text-[hsl(var(--bc)/var(--tw-text-opacity,1))] hover:bg-gray-700'}
                  ${label === 'Logout' ? 'mt-2 border-t border-gray-700 pt-2' : ''}
                  `}
                      onClick={label === 'Delete Project' ? () => setShowDeleteModal(true) : () => handleAction(action)}
                    >
                      <div className='mr-2'>{icon}</div>
                      {label}
                    </button>
                  </li>
                )
            )}
          </ul>
        </nav>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          confirmText='Delete'
          cancelText='Cancel'
          onConfirm={() => handleAction('delete')}
          onCancel={() => setShowDeleteModal(false)}
        >
          <p className='text-center text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-gray-200'>Delete this Project?</p>
        </ConfirmationModal>
      )}
    </div>
  )
}

export { BurgerMenu }
